import React, { useState, useEffect, useMemo, useRef } from 'react';
import Alert from './Alert';
import apiService from '../../services/ApiService';
import publicAccessService from '../../services/PublicAccessService';
import RegionalService from '../../services/RegionalService';
import {
  buildAveChatModelOptions,
  buildRagFlowModelLabel,
  findSupportedAveChatModel,
  getAveChatDefaultModelId,
  normalizeRagFlowModelsResponse
} from '../../utils/ragFlowModels';
import {
  getAssistantProcessingDetail,
  hasAssistantProcessingWarning,
  isRagFlowAssistant
} from '../../utils/aveProcessing';
import './ChatModal.css';

const resolveAssistantId = (assistant) =>
  assistant?.idOpenAIAssistant ||
  assistant?.IdOpenAIAssistant ||
  assistant?.idAssistant ||
  assistant?.IdAssistant ||
  assistant?.idRagFlowChat ||
  assistant?.IdRagFlowChat ||
  assistant?.id ||
  assistant?.Id;

const resolveAssistantName = (assistant, headerTitle) =>
  headerTitle ||
  assistant?.nombreAsistente ||
  assistant?.NombreAsistente ||
  assistant?.nombre ||
  assistant?.Nombre ||
  assistant?.displayName ||
  assistant?.DisplayName ||
  assistant?.email ||
  assistant?.Email ||
  'Asistente';

const isFeatureFlagActive = (flags, flagName) => {
  const normalizedTarget = flagName.toLowerCase();
  return flags.some((flag) => {
    const nombre = (flag?.nombre || flag?.Nombre || '').toLowerCase();
    const activo = flag?.activo ?? flag?.Activo;
    return nombre === normalizedTarget && Boolean(activo);
  });
};

const getSpeechRecognitionConstructor = () => {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const canUseSpeechRecognition = () =>
  typeof navigator !== 'undefined' &&
  Boolean(navigator.mediaDevices?.getUserMedia) &&
  Boolean(getSpeechRecognitionConstructor());

const canUseSpeechSynthesis = () =>
  typeof window !== 'undefined' &&
  typeof window.speechSynthesis !== 'undefined' &&
  typeof window.SpeechSynthesisUtterance !== 'undefined';

const assistantUsesRagFlow = (assistant) =>
  isRagFlowAssistant(assistant) ||
  Boolean(assistant?.idRagFlowChat || assistant?.IdRagFlowChat || assistant?.idRagFlowDataset || assistant?.IdRagFlowDataset);

const resolveAssistantRagFlowModel = (assistant) =>
  assistant?.ragFlowModelo ||
  assistant?.RagFlowModelo ||
  assistant?.modelo ||
  assistant?.Modelo ||
  '';

const resolveAssistantRegionalId = (assistant) =>
  assistant?.idRegional ||
  assistant?.IdRegional ||
  assistant?.regionalId ||
  assistant?.RegionalId ||
  '';

const speechBlockTags = new Set([
  'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DIV', 'DL', 'FIELDSET',
  'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3', 'H4', 'H5',
  'H6', 'HEADER', 'HR', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION',
  'TABLE', 'TR', 'UL'
]);

const removeSourceReferencesFromSpeech = (text) =>
  String(text || '')
    .replace(/\r/g, '\n')
    .replace(/\s*fuentes?\s*:[^\n]*(?=\n|$)/gi, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();

const htmlToPlainText = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return removeSourceReferencesFromSpeech(String(html).replace(/<[^>]+>/g, '\n')).replace(/\s+/g, ' ').trim();
  }

  const parsedDocument = new window.DOMParser().parseFromString(String(html), 'text/html');
  parsedDocument.querySelectorAll('script, style, audio, video').forEach((node) => node.remove());
  parsedDocument.querySelectorAll('p, li, span').forEach((node) => {
    if (/^\s*fuentes?\s*:/i.test(node.textContent || '')) {
      node.remove();
    }
  });

  const collectText = (node) => {
    if (!node) return '';
    if (node.nodeType === window.Node.TEXT_NODE) {
      return node.textContent || '';
    }
    if (node.nodeType !== window.Node.ELEMENT_NODE && node.nodeType !== window.Node.DOCUMENT_NODE) {
      return '';
    }

    const tagName = node.tagName || '';
    if (tagName === 'BR') {
      return '\n';
    }

    const text = Array.from(node.childNodes).map(collectText).join('');
    return speechBlockTags.has(tagName) ? `\n${text}\n` : text;
  };

  return removeSourceReferencesFromSpeech(collectText(parsedDocument.body)).replace(/\s+/g, ' ').trim();
};

const htmlToCopyText = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return String(html).replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();
  }

  const parsedDocument = new window.DOMParser().parseFromString(String(html), 'text/html');
  parsedDocument.querySelectorAll('script, style, audio, video').forEach((node) => node.remove());

  const collectText = (node) => {
    if (!node) return '';
    if (node.nodeType === window.Node.TEXT_NODE) {
      return node.textContent || '';
    }
    if (node.nodeType !== window.Node.ELEMENT_NODE && node.nodeType !== window.Node.DOCUMENT_NODE) {
      return '';
    }

    const tagName = node.tagName || '';
    if (tagName === 'BR') {
      return '\n';
    }

    const text = Array.from(node.childNodes).map(collectText).join('');
    return speechBlockTags.has(tagName) ? `\n${text}\n` : text;
  };

  return collectText(parsedDocument.body)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const appendPublicAccessTokenToUrl = (href) => {
  const token = publicAccessService.getToken();
  if (!href || !token) return href;

  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('publicAccessToken', token);
    return url.toString();
  } catch (error) {
    const separator = href.includes('?') ? '&' : '?';
    return `${href}${separator}publicAccessToken=${encodeURIComponent(token)}`;
  }
};

const ChatModal = ({ isOpen, onClose, assistant, publicChat = false, regionalId, areaId, password, headerTitle }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [showAudioToggle, setShowAudioToggle] = useState(false);
  const [audioReplyEnabled, setAudioReplyEnabled] = useState(false);
  const [showAudioInput, setShowAudioInput] = useState(false);
  const [selectedAveModel, setSelectedAveModel] = useState('');
  const [showAveModelInfo, setShowAveModelInfo] = useState(false);
  const [aveModelCatalog, setAveModelCatalog] = useState([]);
  const [aveModelsLoading, setAveModelsLoading] = useState(false);
  const [aveModelsError, setAveModelsError] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTranscript, setRecordingTranscript] = useState('');
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [isSpeakingAudio, setIsSpeakingAudio] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [isSpeechPaused, setIsSpeechPaused] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserFrameRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const interimTranscriptRef = useRef('');
  const discardRecordingRef = useRef(false);
  const audioUtteranceRef = useRef(null);
  const copyFeedbackTimeoutRef = useRef(null);
  const hasDataSourceWarning = hasAssistantProcessingWarning(assistant);
  const dataSourceWarningDetail = getAssistantProcessingDetail(assistant);
  const isAveAssistant = assistantUsesRagFlow(assistant);
  const assistantDefaultModel = resolveAssistantRagFlowModel(assistant);
  const aveModelOptions = useMemo(
    () => buildAveChatModelOptions(aveModelCatalog, assistantDefaultModel),
    [aveModelCatalog, assistantDefaultModel]
  );
  const selectedAveModelConfig = useMemo(
    () => aveModelOptions.find((model) => model.id === selectedAveModel) ||
      findSupportedAveChatModel(aveModelOptions, selectedAveModel) ||
      null,
    [aveModelOptions, selectedAveModel]
  );

  // Auto scroll al final cuando hay nuevos mensajes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 220)}px`;
  }, [inputMessage, isOpen]);

  const stopMediaStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  const stopVoiceMeter = () => {
    if (analyserFrameRef.current) {
      window.cancelAnimationFrame(analyserFrameRef.current);
      analyserFrameRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    setVoiceLevel(0);
  };

  const cleanupRecording = () => {
    discardRecordingRef.current = true;
    const recognition = speechRecognitionRef.current;

    if (recognition) {
      try {
        recognition.abort();
      } catch (error) {
        console.warn('No se pudo detener el reconocimiento de voz limpiamente:', error);
      }
    }

    speechRecognitionRef.current = null;
    stopVoiceMeter();
    stopMediaStream();
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
    setIsRecording(false);
    setRecordingTranscript('');
  };

  // Inicializar chat cuando se abre el modal
  useEffect(() => {
    if (isOpen && assistant) {
      const assistantFlags = assistant?.featureFlags || assistant?.FeatureFlags || [];
      const flags = assistantFlags.length > 0 ? assistantFlags : RegionalService.getFeatureFlags();
      const isAudioReplyEnabled = isFeatureFlagActive(flags, 'PermiteRespuestaAudioChat');
      const isAudioInputEnabled = isFeatureFlagActive(flags, 'PermiteEntradaAudioChat') && canUseSpeechRecognition();
      setShowAudioToggle(isAudioReplyEnabled);
      setShowAudioInput(isAudioInputEnabled);
      if (!isAudioReplyEnabled) {
        setAudioReplyEnabled(false);
      }
      if (isAveAssistant) {
        loadAveChatModels();
      } else {
        setSelectedAveModel('');
        setShowAveModelInfo(false);
        setAveModelCatalog([]);
        setAveModelsError('');
      }
      initializeChat();
    } else {
      // Limpiar estado cuando se cierra
      cleanupRecording();
      setMessages([]);
      setThreadId(null);
      setIsInitializing(true);
      setInputMessage('');
      setAlert({ show: false, message: '', type: 'error' });
      setShowAudioToggle(false);
      setAudioReplyEnabled(false);
      setShowAudioInput(false);
      setSelectedAveModel('');
      setShowAveModelInfo(false);
      setAveModelCatalog([]);
      setAveModelsError('');
      setAveModelsLoading(false);
      setCopiedMessageId(null);
      stopSpokenResponse();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assistant]);

  useEffect(() => {
    setShowAveModelInfo(false);
  }, [selectedAveModel]);

  useEffect(() => {
    return () => {
      cleanupRecording();
      if (copyFeedbackTimeoutRef.current) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appendTranscribedText = (text) => {
    const cleanedText = (text || '').trim();
    if (!cleanedText) return;

    setInputMessage((previous) => {
      const trimmedPrevious = previous.trimEnd();
      return trimmedPrevious ? `${trimmedPrevious}\n${cleanedText}` : cleanedText;
    });

    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const loadAveChatModels = async () => {
    if (!assistantUsesRagFlow(assistant)) {
      return;
    }

    setAveModelsLoading(true);
    setAveModelsError('');

    try {
      const activeRegionalId = regionalId || resolveAssistantRegionalId(assistant) || RegionalService.getActiveRegionalId();
      const query = activeRegionalId
        ? `${publicChat ? 'regionalId' : 'idRegional'}=${encodeURIComponent(activeRegionalId)}`
        : '';
      const endpoint = publicChat
        ? `/Asistente/publico/ragflow/modelos${query ? `?${query}` : ''}`
        : `/Asistente/ragflow/modelos${query ? `?${query}` : ''}`;
      const response = publicChat
        ? await apiService.getPublic(endpoint)
        : await apiService.get(endpoint);
      const modelos = normalizeRagFlowModelsResponse(response);

      setAveModelCatalog(modelos);
      setSelectedAveModel(getAveChatDefaultModelId(modelos, resolveAssistantRagFlowModel(assistant)));
    } catch (error) {
      console.error('❌ Error al cargar modelos AVE:', error);
      setAveModelCatalog([]);
      setSelectedAveModel('');
      setAveModelsError('No se pudieron cargar los modelos AVE.');
    } finally {
      setAveModelsLoading(false);
    }
  };

  const startVoiceMeter = (stream) => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    audioContextRef.current = audioContext;

    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let index = 0; index < data.length; index += 1) {
        const value = (data[index] - 128) / 128;
        sum += value * value;
      }

      const rms = Math.sqrt(sum / data.length);
      setVoiceLevel(Math.min(1, rms * 5));
      analyserFrameRef.current = window.requestAnimationFrame(tick);
    };

    tick();
  };

  const finishSpeechRecognition = () => {
    const shouldDiscard = discardRecordingRef.current;
    discardRecordingRef.current = false;

    const finalText = finalTranscriptRef.current.trim();
    const interimText = interimTranscriptRef.current.trim();
    const textToAppend = finalText || interimText;

    speechRecognitionRef.current = null;
    stopVoiceMeter();
    stopMediaStream();
    setIsRecording(false);
    setRecordingTranscript('');
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';

    if (!shouldDiscard && textToAppend) {
      appendTranscribedText(textToAppend);
    }
  };

  const stopSpokenResponse = () => {
    if (canUseSpeechSynthesis()) {
      window.speechSynthesis.cancel();
    }
    audioUtteranceRef.current = null;
    setIsSpeakingAudio(false);
    setSpeakingMessageId(null);
    setIsSpeechPaused(false);
  };

  const speakResponseInBrowser = (htmlContent, messageId = null) => {
    if (!canUseSpeechSynthesis()) {
      setAlert({
        show: true,
        message: 'Tu navegador no permite reproducir la respuesta como audio.',
        type: 'error'
      });
      return;
    }

    const text = htmlToPlainText(htmlContent);
    if (!text) return;

    stopSpokenResponse();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = 'es-AR';
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.onend = () => {
      if (audioUtteranceRef.current === utterance) {
        audioUtteranceRef.current = null;
        setIsSpeakingAudio(false);
        setSpeakingMessageId(null);
        setIsSpeechPaused(false);
      }
    };
    utterance.onerror = () => {
      if (audioUtteranceRef.current === utterance) {
        audioUtteranceRef.current = null;
      }
      setIsSpeakingAudio(false);
      setSpeakingMessageId(null);
      setIsSpeechPaused(false);
    };

    audioUtteranceRef.current = utterance;
    setIsSpeakingAudio(true);
    setSpeakingMessageId(messageId);
    setIsSpeechPaused(false);
    window.speechSynthesis.speak(utterance);
  };

  const toggleBrowserAudio = (message) => {
    if (!canUseSpeechSynthesis()) {
      setAlert({
        show: true,
        message: 'Tu navegador no permite reproducir la respuesta como audio.',
        type: 'error'
      });
      return;
    }

    const isCurrentMessage = speakingMessageId === message.id && isSpeakingAudio;
    if (isCurrentMessage) {
      if (isSpeechPaused || window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
        setIsSpeechPaused(false);
      } else {
        window.speechSynthesis.pause();
        setIsSpeechPaused(true);
      }
      return;
    }

    speakResponseInBrowser(message.audio?.text || message.content, message.id);
  };

  const startRecording = async () => {
    const SpeechRecognition = getSpeechRecognitionConstructor();
    if (!canUseSpeechRecognition() || !SpeechRecognition) {
      setAlert({
        show: true,
        message: 'Tu navegador no permite dictado por voz desde esta pantalla.',
        type: 'error'
      });
      return;
    }

    try {
      setAlert({ show: false, message: '', type: 'error' });
      discardRecordingRef.current = false;
      finalTranscriptRef.current = '';
      interimTranscriptRef.current = '';
      setRecordingTranscript('');

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      startVoiceMeter(stream);

      const recognition = new SpeechRecognition();
      recognition.lang = 'es-AR';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        let interimText = '';
        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const transcript = event.results[index][0]?.transcript || '';
          if (event.results[index].isFinal) {
            finalTranscriptRef.current = `${finalTranscriptRef.current} ${transcript}`.trim();
          } else {
            interimText += transcript;
          }
        }

        interimTranscriptRef.current = interimText.trim();
        setRecordingTranscript(`${finalTranscriptRef.current} ${interimText}`.trim());
      };

      recognition.onerror = (event) => {
        if (!['aborted', 'no-speech'].includes(event.error)) {
          setAlert({
            show: true,
            message: 'No se pudo interpretar el audio. Probá nuevamente.',
            type: 'error'
          });
        }
      };

      recognition.onend = finishSpeechRecognition;

      speechRecognitionRef.current = recognition;
      recognition.start();
      setIsRecording(true);
    } catch (error) {
      console.error('❌ Error al iniciar grabación:', error);
      cleanupRecording();
      setAlert({
        show: true,
        message: 'No se pudo acceder al micrófono.',
        type: 'error'
      });
    }
  };

  const stopRecording = () => {
    const recognition = speechRecognitionRef.current;
    if (!recognition) {
      finishSpeechRecognition();
      return;
    }

    discardRecordingRef.current = false;
    recognition.stop();
  };

  const handleRecordButtonClick = () => {
    if (isRecording) {
      stopRecording();
      return;
    }

    startRecording();
  };

  const initializeChat = async () => {
    try {
      setIsInitializing(true);
      setAlert({ show: false, message: '', type: 'error' });
      
      const assistantId = resolveAssistantId(assistant);
      if (!assistantId) {
        throw new Error('No se encontró el asistente seleccionado');
      }

      if (publicChat && !regionalId) {
        throw new Error('Falta el regional para iniciar la conversación');
      }
      if (publicChat && !areaId) {
        throw new Error('Falta el área para iniciar la conversación');
      }

      if (publicChat) {
        const response = await apiService.postPublic('/Asistente/publico/threads', {
          idOpenAIAssistant: assistantId,
          regionalId,
          areaId,
          password
        });

        if (response.ok && response.threadId) {
          setThreadId(response.threadId);
        } else {
          throw new Error(response.error || 'Error al inicializar la conversación');
        }
      } else {
        const response = await apiService.post('/Threads/threads', {
          idOpenAIAssistant: assistantId
        });

        if (response.ok && response.threadId) {
          setThreadId(response.threadId);
        } else {
          throw new Error(response.error || 'Error al inicializar la conversación');
        }
      }

      setMessages([]);
      
    } catch (error) {
      console.error('❌ Error al inicializar chat:', error);
      setAlert({
        show: true,
        message: error.message || 'Error al inicializar la conversación',
        type: 'error'
      });
    } finally {
      setIsInitializing(false);
    }
  };

  // Función para extraer HTML del bloque de código markdown
  const extractHtmlFromMarkdown = (content) => {
    
    // Si el contenido viene envuelto en ```html ... ```
    const htmlBlockMatch = content.match(/```html\s*\n([\s\S]*?)\n```/);
    if (htmlBlockMatch) {
      const htmlContent = htmlBlockMatch[1];
      return extractBodyContent(htmlContent);
    }
    
    // Si viene envuelto en ``` ... ``` (sin especificar html)
    const genericBlockMatch = content.match(/```\s*\n([\s\S]*?)\n```/);
    if (genericBlockMatch) {
      const innerContent = genericBlockMatch[1];
      // Verificar si parece HTML
      if (innerContent.includes('<html>') || innerContent.includes('<!DOCTYPE')) {
        return extractBodyContent(innerContent);
      }
    }
    
    // Si no hay bloque de código pero parece HTML completo
    if (content.includes('<!DOCTYPE') || content.includes('<html>')) {
      return extractBodyContent(content);
    }
    
    return content;
  };

  // Función para extraer solo el contenido del body de un HTML completo
  const extractBodyContent = (htmlString) => {
    // Buscar el contenido dentro de las etiquetas <body>
    const bodyMatch = htmlString.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
      return bodyMatch[1].trim();
    }
    
    // Si no hay body, pero hay html, buscar después del head
    const afterHeadMatch = htmlString.match(/<\/head>\s*([\s\S]*?)<\/html>/i);
    if (afterHeadMatch) {
      return afterHeadMatch[1].trim();
    }
    
    // Si no encuentra estructura HTML completa, retornar tal como está
    return htmlString;
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || !threadId || isLoading || isRecording) return;

    const userMessage = {
      id: 'user-' + Date.now(),
      content: inputMessage,
      role: 'user',
      timestamp: new Date()
    };

    const messageToSend = inputMessage.trim();
    const shouldUseBrowserAudioReply = audioReplyEnabled && isAveAssistant;
    const shouldRequestServerAudio = audioReplyEnabled && !isAveAssistant;
    
    // Agregar mensaje del usuario inmediatamente
    stopSpokenResponse();
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      
      // Llamada real a la API
      const assistantId = resolveAssistantId(assistant);
      if (!assistantId) {
        throw new Error('No se encontró el asistente seleccionado');
      }

      if (publicChat && !regionalId) {
        throw new Error('Falta el regional para enviar el mensaje');
      }
      if (publicChat && !areaId) {
        throw new Error('Falta el área para enviar el mensaje');
      }

      const response = publicChat
        ? await apiService.postPublic('/Asistente/publico/chat', {
            idAsistente: assistantId,
            idThread: threadId,
            mensaje: messageToSend,
            regionalId,
            areaId,
            password,
            permiteRespuestaAudioChat: shouldRequestServerAudio,
            ragFlowModelo: isAveAssistant ? selectedAveModel : undefined
          })
        : await apiService.post('/Asistente/chat', {
            idAsistente: assistantId,
            idThread: threadId,
            mensaje: messageToSend,
            permiteRespuestaAudioChat: shouldRequestServerAudio,
            ragFlowModelo: isAveAssistant ? selectedAveModel : undefined
          });


      if (response.ok && response.respuesta) {
        // Extraer HTML del bloque de código markdown si es necesario
        const htmlContent = extractHtmlFromMarkdown(response.respuesta);
        const archivoIds = response.archivoIds || response.ArchivoIds || [];
        const archivoLinks = response.archivoLinks || response.ArchivoLinks || [];
        const normalizedArchivoLinks = Array.isArray(archivoLinks)
          ? (publicChat ? archivoLinks.map(appendPublicAccessTokenToUrl) : archivoLinks)
          : [];
        const hasServerLinks = normalizedArchivoLinks.length > 0;
        const shouldStripLinks = hasServerLinks || /\/mnt\/data\//i.test(htmlContent);
        const safeHtmlContent = shouldStripLinks
          ? htmlContent.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, '')
          : htmlContent;
        const downloadLinks = hasServerLinks
          ? `
            <div class="download-box">
              <p><strong>Archivos generados</strong></p>
              <ul>
                ${normalizedArchivoLinks.map((href, idx) => {
                  return `<li><a href="${href}" target="_blank" rel="noreferrer">Descargar archivo ${idx + 1}</a></li>`;
                }).join('')}
              </ul>
            </div>
          `
          : (Array.isArray(archivoIds) && archivoIds.length > 0
            ? `
              <div class="download-box">
                <p><strong>Archivos generados</strong></p>
                <ul>
                  ${archivoIds.map((fileId, idx) => {
                    const href = publicChat
                      ? appendPublicAccessTokenToUrl(`${apiService.baseURL}/Asistente/publico/archivos/${fileId}/download?regionalId=${regionalId}`)
                      : `${apiService.baseURL}/Asistente/archivos/${fileId}/download`;
                    return `<li><a href="${href}" target="_blank" rel="noreferrer">Descargar archivo ${idx + 1}</a></li>`;
                  }).join('')}
                </ul>
              </div>
            `
            : '');
        const audioBase64 = response.audioBase64 || response.AudioBase64;
        const audioContentType = response.audioContentType || response.AudioContentType || 'audio/mpeg';
        const audioSrc = audioBase64 ? `data:${audioContentType};base64,${audioBase64}` : '';
        const audioExtension = audioContentType.includes('wav')
          ? 'wav'
          : audioContentType.includes('ogg')
            ? 'ogg'
            : 'mp3';
        const audio = audioSrc
          ? {
              mode: 'file',
              src: audioSrc,
              downloadName: `respuesta.${audioExtension}`
            }
          : (shouldUseBrowserAudioReply
            ? {
                mode: 'browser',
                text: safeHtmlContent
              }
            : null);
        const finalContent = `${safeHtmlContent}${downloadLinks}`;
        
        const assistantMessage = {
          id: response.idMensaje || 'assistant-' + Date.now(),
          content: finalContent,
          audio,
          role: 'assistant',
          timestamp: new Date(response.fechaRespuesta || new Date())
        };
        
        setMessages(prev => [...prev, assistantMessage]);

        if (shouldUseBrowserAudioReply) {
          speakResponseInBrowser(safeHtmlContent, assistantMessage.id);
        }
        
      } else {
        throw new Error(response.error || 'Error al procesar la respuesta del asistente');
      }
      
    } catch (error) {
      console.error('❌ Error al enviar mensaje:', error);
      
      // Mostrar mensaje de error en el chat
      const errorMessage = {
        id: 'error-' + Date.now(),
        content: `<div class="error-message">
          <p><strong>❌ Error al enviar mensaje</strong></p>
          <p>Lo siento, no pude procesar tu mensaje. Por favor, inténtalo nuevamente.</p>
          <p><em>Detalles: ${error.message}</em></p>
        </div>`,
        role: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      
      // También mostrar alerta
      setAlert({
        show: true,
        message: `Error al enviar mensaje: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getMessageCopyText = (message) => {
    if (message.role === 'assistant') {
      return htmlToCopyText(message.content);
    }

    return String(message.content || '').trim();
  };

  const writeTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const copyMessageToClipboard = async (message) => {
    const text = getMessageCopyText(message);
    if (!text) return;

    try {
      await writeTextToClipboard(text);
      setCopiedMessageId(message.id);
      if (copyFeedbackTimeoutRef.current) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
      copyFeedbackTimeoutRef.current = window.setTimeout(() => {
        setCopiedMessageId(null);
      }, 1600);
    } catch (error) {
      console.error('❌ Error al copiar mensaje:', error);
      setAlert({
        show: true,
        message: 'No se pudo copiar el mensaje al portapapeles.',
        type: 'error'
      });
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Función para sanitizar HTML básico (opcional, para mayor seguridad)
  const sanitizeHtml = (html) => {
    // Lista básica de tags permitidos para mensajes del asistente
    // eslint-disable-next-line no-unused-vars
    const allowedTags = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 
                        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre', 'blockquote', 
                        'a', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'hr', 'mark', 'div'];
    
    // Por ahora retornamos el HTML tal como viene
    // En producción podrías usar una librería como DOMPurify para filtrar por allowedTags
    return html;
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  if (!isOpen) return null;

  return (
    <div className="chat-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`chat-modal${publicChat ? ' public-chat' : ''}`}>
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="assistant-avatar">
              <i className="fas fa-robot"></i>
            </div>
            <div className="assistant-info">
              <h3 className="assistant-name">
                {resolveAssistantName(assistant, headerTitle)}
              </h3>
              <p className="assistant-status">
                {isInitializing ? 'Inicializando...' : 
                 threadId ? 'Conectado' : 'Sin conexión'}
              </p>
            </div>
          </div>
          
          <button className="close-btn" onClick={onClose} aria-label="Cerrar chat" title="Cerrar chat">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Alert */}
        {alert.show && (
          <div className="chat-alert">
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          </div>
        )}

        {hasDataSourceWarning && (
          <div className="chat-datasource-warning" title={dataSourceWarningDetail}>
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
            <span>
              {dataSourceWarningDetail || 'Este asistente tiene advertencias en uno o mas archivos del datasource.'}
            </span>
          </div>
        )}

        {/* Messages */}
        <div className="chat-messages">
          {isInitializing ? (
            <div className="initializing-message">
              <div className="thinking-content">
                <div className="thinking-avatar">
                  <i className="fas fa-cog fa-spin"></i>
                </div>
                <div className="thinking-text">
                  <span className="thinking-label">
                    Inicializando conversación con {resolveAssistantName(assistant, headerTitle)}...
                  </span>
                  <div className="typing-indicator">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.length === 0 && !isLoading && (
                <div className="chat-empty-state">
                  <div className="chat-empty-avatar">
                    <i className="fas fa-robot"></i>
                  </div>
                  <h2>{resolveAssistantName(assistant, headerTitle)}</h2>
                  <p>Listo para conversar</p>
                </div>
              )}

              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.role === 'user' ? 'user-message' : 
                             message.role === 'system' ? 'system-message' : 'assistant-message'}`}
                >
                  <div className="message-content">
                    <div className="message-bubble">
                      <div
                        className="message-text"
                        dangerouslySetInnerHTML={{
                          __html: message.role === 'assistant'
                            ? sanitizeHtml(message.content)
                            : message.content.replace(/\n/g, '<br>')
                        }}
                      />
                      <button
                        type="button"
                        className={`message-copy-btn${copiedMessageId === message.id ? ' copied' : ''}`}
                        onClick={() => copyMessageToClipboard(message)}
                        aria-label={copiedMessageId === message.id ? 'Mensaje copiado' : 'Copiar mensaje'}
                        title={copiedMessageId === message.id ? 'Copiado' : 'Copiar mensaje'}
                      >
                        <i className={`fas ${copiedMessageId === message.id ? 'fa-check' : 'fa-copy'}`}></i>
                      </button>
                    </div>
                    {message.role === 'assistant' && message.audio && (
                      <div className="message-audio-controls">
                        {message.audio.mode === 'file' ? (
                          <>
                            <audio controls src={message.audio.src}></audio>
                            <a
                              className="message-audio-action"
                              href={message.audio.src}
                              download={message.audio.downloadName || 'respuesta.mp3'}
                            >
                              <i className="fas fa-download"></i>
                              <span>Descargar</span>
                            </a>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="message-audio-action"
                              onClick={() => toggleBrowserAudio(message)}
                              aria-label={
                                speakingMessageId === message.id && isSpeakingAudio && !isSpeechPaused
                                  ? 'Pausar audio'
                                  : 'Reproducir audio'
                              }
                            >
                              <i
                                className={`fas ${
                                  speakingMessageId === message.id && isSpeakingAudio && !isSpeechPaused
                                    ? 'fa-pause'
                                    : 'fa-play'
                                }`}
                              ></i>
                              <span>
                                {speakingMessageId === message.id && isSpeakingAudio && !isSpeechPaused
                                  ? 'Pausar'
                                : 'Reproducir'}
                              </span>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {message.timestamp && (
                      <div className="message-time">
                        {formatTime(message.timestamp)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="message assistant-message thinking-message">
                  <div className="message-content">
                    <div className="message-text thinking-bubble">
                      <div className="thinking-content">
                        <div className="thinking-avatar">
                          <i className="fas fa-robot"></i>
                        </div>
                        <div className="thinking-text">
                          <span className="thinking-label">
                            {resolveAssistantName(assistant, headerTitle)} está escribiendo
                          </span>
                          <div className="typing-indicator">
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                            <div className="typing-dot"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="chat-input-area">
          {(showAudioToggle || isAveAssistant) && (
            <div className="chat-options">
              {isAveAssistant && (
                <div className="ave-model-options">
                  <label className="ave-model-field">
                    <span>Modelo AVE</span>
                    <select
                      value={selectedAveModel}
                      onChange={(e) => setSelectedAveModel(e.target.value)}
                      disabled={isLoading || isInitializing || aveModelsLoading || aveModelOptions.length === 0}
                    >
                      {aveModelsLoading && (
                        <option value="">Cargando modelos...</option>
                      )}
                      {!aveModelsLoading && aveModelOptions.length === 0 && (
                        <option value="">Sin modelos activos</option>
                      )}
                      {!aveModelsLoading && aveModelOptions.map((model) => (
                        <option key={model.id} value={model.id}>
                          {buildRagFlowModelLabel(model)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className={`ave-model-info-btn${showAveModelInfo ? ' active' : ''}`}
                      onClick={() => setShowAveModelInfo((current) => !current)}
                      disabled={!selectedAveModelConfig}
                      aria-label="Ver características del modelo"
                      title="Ver características del modelo"
                    >
                      <i className="fas fa-info"></i>
                    </button>
                  </label>
                  {aveModelsError && (
                    <div className="ave-model-error">
                      {aveModelsError}
                    </div>
                  )}
                  {showAveModelInfo && selectedAveModelConfig && (
                    <div className="ave-model-info-panel">
                      <div className="ave-model-info-title">
                        {buildRagFlowModelLabel(selectedAveModelConfig)}
                      </div>
                      {selectedAveModelConfig.descripcion && (
                        <p className="ave-model-description">
                          {selectedAveModelConfig.descripcion}
                        </p>
                      )}
                      {selectedAveModelConfig.topN && (
                        <p className="ave-model-context">
                          Contexto recuperado: {selectedAveModelConfig.topN} fragmentos relevantes por consulta.
                        </p>
                      )}
                      {Array.isArray(selectedAveModelConfig.caracteristicas) && selectedAveModelConfig.caracteristicas.length > 0 ? (
                        <ul>
                          {selectedAveModelConfig.caracteristicas.map((caracteristica, index) => (
                            <li key={`${selectedAveModelConfig.id || selectedAveModel}-${index}`}>
                              {caracteristica}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>Sin características adicionales disponibles.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {showAudioToggle && (
                <label className="audio-toggle">
                  <input
                    type="checkbox"
                    checked={audioReplyEnabled}
                    onChange={(e) => setAudioReplyEnabled(e.target.checked)}
                  />
                  <span>Respuesta en forma de audio</span>
                </label>
              )}
            </div>
          )}
          {isRecording && (
            <div className="voice-capture-status" role="status" aria-live="polite">
              <div className="voice-meter" aria-hidden="true">
                {[0.35, 0.65, 1, 0.65, 0.35].map((weight, index) => (
                  <span
                    key={weight + index}
                    style={{ height: `${Math.max(7, Math.round(8 + voiceLevel * 28 * weight))}px` }}
                  />
                ))}
              </div>
              <span className="voice-capture-text">
                {recordingTranscript || 'Escuchando...'}
              </span>
            </div>
          )}
          <div className={`chat-input-container${showAudioInput ? ' has-voice-input' : ''}`}>
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                isInitializing
                  ? "Inicializando..."
                  : isRecording
                    ? "Grabando..."
                    : `Mensaje a ${resolveAssistantName(assistant, headerTitle)}`
              }
              disabled={isLoading || isInitializing || !threadId}
              rows={1}
              className="chat-input"
            />
            {showAudioInput && (
              <button
                type="button"
                onClick={handleRecordButtonClick}
                disabled={!isRecording && (isLoading || isInitializing || !threadId)}
                className={`voice-input-btn${isRecording ? ' recording' : ''}`}
                aria-label={isRecording ? 'Detener grabación' : 'Grabar audio'}
                title={isRecording ? 'Detener grabación' : 'Grabar audio'}
              >
                {isRecording ? (
                  <i className="fas fa-stop"></i>
                ) : (
                  <i className="fas fa-microphone"></i>
                )}
              </button>
            )}
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading || isInitializing || !threadId || isRecording}
              className="send-btn"
            >
              {isLoading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <i className="fas fa-paper-plane"></i>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
